import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const AdminSpeakersPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchSpeakers();
  }, []);

  const fetchSpeakers = async () => {
    try {
      const { data, error } = await supabase
        .from('speakers')
        .select('*')
        .order('name');

      if (error) throw error;
      setSpeakers(data || []);
    } catch (error) {
      console.error('Error fetching speakers:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSpeakers = speakers.filter(speaker => {
    return speaker.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           speaker.organization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           speaker.title?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleDelete = async (speakerId) => {
    if (window.confirm('Are you sure you want to delete this speaker?')) {
      try {
        const { error } = await supabase
          .from('speakers')
          .delete()
          .eq('id', speakerId);

        if (error) throw error;
        setSpeakers(prev => prev.filter(s => s.id !== speakerId));
      } catch (error) {
        console.error('Error deleting speaker:', error);
        alert('Failed to delete speaker');
      }
    }
  };

  const toggleFeatured = async (speakerId) => {
    const speaker = speakers.find(s => s.id === speakerId);
    try {
      const { error } = await supabase
        .from('speakers')
        .update({ is_featured: !speaker.is_featured })
        .eq('id', speakerId);

      if (error) throw error;
      setSpeakers(prev => prev.map(s => 
        s.id === speakerId ? { ...s, is_featured: !s.is_featured } : s
      ));
    } catch (error) {
      console.error('Error updating speaker:', error);
    }
  };

  const openEditModal = (speaker) => {
    setSelectedSpeaker(speaker);
    setEditForm({
      name: speaker.name || '',
      title: speaker.title || '',
      church_name: speaker.church_name || '',
      bio: speaker.bio || '',
      website_url: speaker.website_url || ''
    });
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!selectedSpeaker) return;
    try {
      const { error } = await supabase
        .from('speakers')
        .update(editForm)
        .eq('id', selectedSpeaker.id);

      if (error) throw error;
      await fetchSpeakers();
      setShowEditModal(false);
      setSelectedSpeaker(null);
    } catch (error) {
      console.error('Error updating speaker:', error);
      alert('Failed to update speaker');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Speakers</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage speaker profiles and information.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <Link
            to="/admin/speakers/new"
            className="inline-flex items-center px-4 py-2 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
            Add Speaker
          </Link>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search speakers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Speakers Grid */}
      {filteredSpeakers.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-12 text-center">
          <svg className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">No speakers found</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-4">
            {searchTerm ? 'Try adjusting your search term' : 'Add your first speaker to get started'}
          </p>
          {!searchTerm && (
            <Link to="/admin/speakers/create" className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Speaker
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSpeakers.map((speaker) => (
          <div 
            key={speaker.id} 
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="relative">
              <div className="h-32 bg-gradient-to-br from-primary to-primary-dark"></div>
              <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2">
                <div className="w-24 h-24 rounded-full border-4 border-white dark:border-gray-800 overflow-hidden bg-gray-200">
                  {speaker.image ? (
                    <img 
                      src={speaker.image} 
                      alt={speaker.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(speaker.name)}&background=7c3aed&color=fff&size=96`;
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary text-white text-2xl font-bold">
                      {speaker.name?.charAt(0)}
                    </div>
                  )}
                </div>
              </div>
              {speaker.is_featured && (
                <div className="absolute top-3 right-3">
                  <span className="px-2 py-1 bg-secondary text-white text-xs font-medium rounded-full">
                    Featured
                  </span>
                </div>
              )}
            </div>

            <div className="pt-14 pb-4 px-4 text-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{speaker.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{speaker.title}</p>
              {speaker.church_name && (
                <span className="inline-block mt-2 px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-medium rounded-full">
                  {speaker.church_name}
                </span>
              )}

              {speaker.topics && speaker.topics.length > 0 && (
                <div className="mt-3 flex flex-wrap justify-center gap-1">
                  {speaker.topics.slice(0, 3).map((topic, idx) => (
                    <span 
                      key={idx}
                      className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded-full"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="px-4 pb-4 flex justify-center space-x-2">
              <Link
                to={`/speakers/${speaker.id}`}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                View
              </Link>
              <button 
                onClick={() => openEditModal(speaker)}
                className="px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors"
              >
                Edit
              </button>
              <button
                onClick={() => toggleFeatured(speaker.id)}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  speaker.is_featured 
                    ? 'text-secondary bg-secondary/10 hover:bg-secondary/20' 
                    : 'text-gray-500 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {speaker.is_featured ? '★' : '☆'}
              </button>
              <button
                onClick={() => handleDelete(speaker.id)}
                className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
        </div>
      )}

      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
        <p>
          Showing <span className="font-medium">{filteredSpeakers.length}</span> of <span className="font-medium">{speakers.length}</span> speakers
        </p>
        <p>
          <span className="font-medium">{speakers.filter(s => s.is_featured).length}</span> featured
        </p>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Edit Speaker</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={editForm.name || ''}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                <input
                  type="text"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Church Name</label>
                <input
                  type="text"
                  value={editForm.church_name || ''}
                  onChange={(e) => setEditForm({...editForm, church_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Website</label>
                <input
                  type="url"
                  value={editForm.website_url || ''}
                  onChange={(e) => setEditForm({...editForm, website_url: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bio</label>
                <textarea
                  value={editForm.bio || ''}
                  onChange={(e) => setEditForm({...editForm, bio: e.target.value})}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSpeakersPage;
